/* CUSTOM — product page behaviour.
   Custom elements rather than DOMContentLoaded handlers, so anything Dawn
   replaces re-upgrades itself where a one-shot init would not.

   Worth knowing what Dawn actually replaces on a variant change: it fetches the
   section's new HTML and then swaps a fixed list of targets out of it — the
   variant picker, price, media, sku, inventory, volume, price-per-item — and
   nothing else. A custom block's markup is left exactly as first rendered, so
   anything of ours that depends on the selected variant has to update itself
   from the variantChange event, which carries that fetched document. */

if (!customElements.get('subscription-selector')) {
  customElements.define(
    'subscription-selector',
    class SubscriptionSelector extends HTMLElement {
      connectedCallback() {
        this.planInput =
          this.querySelector('[data-mg-selling-plan]') ||
          document.getElementById(this.dataset.formId)?.querySelector('[data-mg-selling-plan]');
        this.frequency = this.querySelector('[data-mg-frequency]');
        this.subPriceEl = this.querySelector('[data-mg-sub-price]');
        this.commitmentEl = this.querySelector('[data-mg-commitment]');
        /* The unsubstituted wording, carried as an attribute. Reading it back
           off the rendered text would mean pattern-matching whichever interval
           is currently named, which breaks as soon as the merchant edits the
           sentence. */
        this.commitmentTemplate = this.commitmentEl?.dataset.template || '';
        this.radios = Array.from(this.querySelectorAll('.mg-purchase__radio'));

        if (!this.planInput || this.radios.length === 0) return;

        const form = document.getElementById(this.dataset.formId);
        if (form && this.planInput.parentElement !== form) form.appendChild(this.planInput);

        this.priceContainer = document.getElementById(this.dataset.priceTarget);
        this.submitButton = document.getElementById(this.dataset.submitTarget);

        /* Cache Dawn's own price markup so switching back to a one-time
           purchase restores it exactly, including any sale badge. */
        if (this.priceContainer) this.originalPriceHTML = this.priceContainer.innerHTML;

        this.radios.forEach((radio) => {
          radio.addEventListener('change', () => this.onPurchaseTypeChange());
        });
        if (this.frequency) {
          this.frequency.addEventListener('change', () => this.onFrequencyChange());
        }

        /* The section may have re-rendered mid-session — reflect whatever is
           checked in the fresh markup rather than assuming the default. */
        this.sync();
      }

      get isSubscribed() {
        const checked = this.radios.find((radio) => radio.checked);
        return Boolean(checked && checked.value === 'subscribe');
      }

      /* The frequency control is a radio group rather than a select, so the
         chosen plan is the checked input, not selectedOptions. */
      get frequencyInput() {
        return this.frequency?.querySelector('.mg-freq__radio:checked') || null;
      }

      /* Named for the sticky bar, which restates the choice made up here. */
      get selectionLabel() {
        if (!this.isSubscribed) return this.dataset.onetimeLabel || '';
        const base = this.dataset.subscribeLabel || '';
        const interval = this.frequencyInput?.dataset.interval;
        return interval ? `${base} · every ${interval}` : base;
      }

      get selectedPrice() {
        if (!this.isSubscribed) return this.dataset.onetimePrice;
        return this.frequencyInput?.dataset.price || this.dataset.onetimePrice;
      }

      onPurchaseTypeChange() {
        this.sync();
      }

      onFrequencyChange() {
        const option = this.frequencyInput;
        if (!option) return;

        this.planInput.value = option.value;
        this.updateCommitment(option);

        /* Keep the collapsed summary price in step with the frequency, so the
           card always shows the price of the plan that is actually selected. */
        if (this.subPriceEl) {
          const was = this.subPriceEl.querySelector('.mg-purchase__was');
          this.subPriceEl.textContent = option.dataset.price;
          if (was && Number(option.dataset.save) > 0) this.subPriceEl.prepend(was);
        }

        this.sync();
      }

      sync() {
        const subscribed = this.isSubscribed;

        this.classList.toggle('mg-purchase--subscribed', subscribed);
        this.querySelectorAll('.mg-purchase__option').forEach((option) => {
          const isActive = option.dataset.mgOption === (subscribed ? 'subscribe' : 'onetime');
          option.classList.toggle('is-selected', isActive);
        });

        /* A disabled input is not submitted at all, which is what a one-time
           purchase needs — an empty selling_plan parameter is not the same
           thing as no selling_plan. */
        const plan = subscribed ? this.frequencyInput?.value || '' : '';
        this.planInput.value = plan;
        this.planInput.setAttribute('value', plan);
        this.planInput.disabled = !subscribed;
        this.planInput.dispatchEvent(new Event('change', { bubbles: true }));

        this.updatePrice();
        this.announce();
      }

      updatePrice() {
        const price = this.selectedPrice;

        /* Published on the element so a later-connecting component — the sticky
           bar — can read the current price without having caught the event. */
        this.dataset.currentPrice = price;
        this.dataset.currentLabel = this.selectionLabel;
        this.dataset.currentSubscribed = String(this.isSubscribed);

        if (this.priceContainer) {
          if (this.isSubscribed) {
            this.priceContainer.innerHTML = `
              <div class="price price--large price--on-sale">
                <div class="price__container">
                  <div class="price__sale">
                    <span class="price-item price-item--sale">${price}</span>
                    <span class="price-item price-item--regular"><s>${this.dataset.onetimePrice}</s></span>
                  </div>
                </div>
              </div>`;
          } else if (this.originalPriceHTML) {
            this.priceContainer.innerHTML = this.originalPriceHTML;
          }
        }

        /* The button keeps its plain label. It used to carry the price too
           ("Add to cart · £14.45"), which the price block and the selected
           purchase row already state directly above it. */
      }

      updateCommitment(option) {
        if (!this.commitmentEl || !this.commitmentTemplate) return;
        const interval = option?.dataset.interval;
        if (!interval) return;
        this.commitmentEl.textContent = this.commitmentTemplate.replace('[interval]', interval);
      }

      announce() {
        this.dispatchEvent(
          new CustomEvent('mg:purchase-changed', {
            bubbles: true,
            detail: {
              subscribed: this.isSubscribed,
              price: this.selectedPrice,
              onetimePrice: this.dataset.onetimePrice,
              label: this.selectionLabel,
            },
          })
        );
      }
    }
  );
}

if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-mg-sticky-add]');
        this.priceEl = this.querySelector('[data-mg-sticky-price]');
        this.planEl = this.querySelector('[data-mg-sticky-plan]');
        this.qtyInput = this.querySelector('[data-mg-qty-input]');
        if (!this.button) return;

        /* The one-time markup as Liquid rendered it, including any compare-at
           strikethrough, so switching back off a subscription restores the
           product's own sale state rather than a flat price. */
        this.basePriceHTML = this.priceEl?.innerHTML || '';

        this.applyVisibilityRules();

        /* The form id is owned by the main product section, which this section
           has no reference to — find it rather than hardcoding a section id
           that would silently break if the section were re-added.
           It must be scoped to <product-form>: Dawn's Shop Pay installment
           block renders a second form that also posts to /cart/add, sits
           earlier in the DOM, and carries neither quantity nor selling plan. */
        this.form = document.querySelector('product-info product-form form');
        this.anchor =
          document.querySelector('product-info .product-form__buttons') ||
          document.querySelector('product-info .product-form');

        if (!this.form || !this.anchor) return;

        this.button.addEventListener('click', () => this.submit());
        document.addEventListener('mg:purchase-changed', (event) => this.onPurchaseChanged(event));

        /* The selector announces its price on its own connectedCallback, which
           runs before this one — the main section is earlier in the document.
           So read the current price directly rather than waiting for an event
           that has already been missed. */
        const selector = document.querySelector('subscription-selector');
        if (selector?.dataset.currentPrice) {
          this.render({
            subscribed: selector.dataset.currentSubscribed === 'true',
            price: selector.dataset.currentPrice,
            onetimePrice: selector.dataset.onetimePrice,
            label: selector.dataset.currentLabel,
          });
        }

        this.bindQuantity();

        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.evaluate();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.evaluate();
        });
      }

      /* Deliberately not an IntersectionObserver. The observer only fires when
         the intersection state changes, so a jump — an anchor link to the
         reviews, or a restored scroll position — can land well past the buy box
         without the state ever having changed, leaving the bar stuck hidden. */
      evaluate() {
        const rect = this.anchor.getBoundingClientRect();
        this.hidden = rect.bottom > 0;
      }

      applyVisibilityRules() {
        if (this.dataset.showOnMobile === 'false') this.classList.add('mg-sticky--no-mobile');
        if (this.dataset.showOnDesktop === 'false') this.classList.add('mg-sticky--no-desktop');
      }


      onPurchaseChanged(event) {
        if (event.detail) this.render(event.detail);
      }

      render({ subscribed, price, onetimePrice, label }) {
        if (this.planEl && label) this.planEl.textContent = label;

        if (!this.priceEl) return;
        if (subscribed && price && onetimePrice) {
          this.priceEl.innerHTML =
            `<span class="mg-sticky__now">${price}</span>` +
            `<s class="mg-sticky__was">${onetimePrice}</s>`;
        } else {
          this.priceEl.innerHTML = this.basePriceHTML;
        }
      }

      /* The bar submits the main form, so the real quantity is the one in the
         buy box. This stepper is a proxy that writes to it — hence the change
         event, which Dawn listens for to update volume pricing. */
      bindQuantity() {
        this.mainQty = document.querySelector('product-info .quantity__input');
        if (!this.qtyInput || !this.mainQty) return;

        this.querySelectorAll('[data-mg-qty-step]').forEach((stepper) => {
          stepper.addEventListener('click', () => {
            this.setQuantity(this.currentQuantity + Number(stepper.dataset.mgQtyStep));
          });
        });

        this.qtyInput.addEventListener('change', () => this.setQuantity(Number(this.qtyInput.value)));
        this.mainQty.addEventListener('change', () => this.mirrorQuantity());
        this.mirrorQuantity();
      }

      get quantityMin() {
        return Number(this.mainQty?.getAttribute('min')) || 1;
      }

      get currentQuantity() {
        const value = Number(this.mainQty?.value);
        return Number.isFinite(value) && value >= this.quantityMin ? value : this.quantityMin;
      }

      setQuantity(next) {
        const value = Number.isFinite(next) && next >= this.quantityMin ? Math.floor(next) : this.quantityMin;
        this.mainQty.value = String(value);
        this.mainQty.dispatchEvent(new Event('change', { bubbles: true }));
        this.qtyInput.value = String(value);
      }

      mirrorQuantity() {
        this.qtyInput.value = String(this.currentQuantity);
      }

      submit() {
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
        } else {
          this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      }
    }
  );
}

/* "What's inside" is a button and a dialog. The dialog is the native <dialog>
   element: showModal() puts it in the browser's top layer, so the sticky buy-box
   column it lives inside cannot clip it, and the focus trap, the Esc key and the
   backdrop are the browser's rather than ours.

   The block also has to follow the variant, because the gift box's contents
   include whichever of Define or Shape is selected. Dawn does not re-render
   custom blocks on a variant change — it fetches the section's new HTML and
   swaps a fixed list of targets out of it, and this is not one of them — so this
   listens for variantChange, which carries that already-fetched document, and
   takes its own markup from it. Server-rendered truth, not price arithmetic
   repeated in JavaScript. */
if (!customElements.get('bundle-contents')) {
  customElements.define(
    'bundle-contents',
    class BundleContents extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          /* The whole strip opens it, not just the View button — the button is
             there to be the control a keyboard and a screen reader can find. */
          if (event.target.closest('[data-mg-bundle-open], .mg-bundle__trigger')) this.open();
          if (event.target.closest('[data-mg-bundle-close]')) this.close();

          /* A click on the backdrop is reported against the dialog itself —
             the panel fills it, so nothing inside the panel ever matches. The
             browser draws the backdrop but does not act on it. */
          if (event.target === this.dialog) this.close();
        });

        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => this.refresh(data));
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      get dialog() {
        return this.querySelector('.mg-bundle__dialog');
      }

      open() {
        const dialog = this.dialog;
        if (dialog && typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      }

      close() {
        const dialog = this.dialog;
        if (dialog && dialog.open) dialog.close();
      }

      /* Dawn's own reading of which section fired, so a quick-add modal's
         variant change can't repaint the block on the page behind it. */
      refresh(data) {
        if (!data || !data.html) return;

        const info = this.closest('product-info');
        const sectionId = info ? info.dataset.originalSection || info.dataset.section : null;
        if (data.sectionId !== sectionId) return;

        const fresh = data.html.querySelector('bundle-contents');
        if (!fresh) return;

        /* Replacing the markup replaces the dialog with a closed one. Nothing
           on the page can change the variant while it is open — showModal makes
           the rest of the document inert — but a variant change from elsewhere
           should not shut it in the reader's face. */
        const wasOpen = this.dialog && this.dialog.open;
        this.innerHTML = fresh.innerHTML;
        if (wasOpen) this.open();
      }
    }
  );
}

/* Dawn learns what a variant change means by fetching the section's HTML, and
   it disables Add to bag for the whole round trip — about 1.2s here, and the
   response is `private, no-store` so it can never be prefetched or cached. On
   the gift box, where the picker is two cards, that reads as the buy box going
   dead every time you change your mind.

   It does not have to. The cards were rendered from the variants themselves, so
   each one already carries its variant id and whether it is in stock: the form
   can be pointed at the new variant and the button given back immediately, and
   Dawn's fetch can land afterwards to do the rest — price, media, URL.

   This runs on document, so it fires after the listener on <variant-selects>
   that disabled the button in the first place. Anything it cannot vouch for —
   a variant out of stock — is left alone for Dawn to answer. */
document.addEventListener('change', (event) => {
  const input = event.target.closest('.mg-variant-card__input');
  if (!input || input.dataset.mgAvailable !== 'true' || !input.dataset.mgVariantId) return;

  const info = input.closest('product-info');
  const form = info && info.querySelector('product-form form');
  if (!form) return;

  const id = form.querySelector('[name="id"]');
  if (id) id.value = input.dataset.mgVariantId;

  const submit = form.querySelector('.product-form__submit');
  if (submit) submit.removeAttribute('disabled');
});

/* Smooth-scroll the star rating to the review widget. Judge.me injects its
   markup late, so the anchor may not exist at click time — fall back to the
   section wrapper, which is server-rendered. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-mg-scroll-to]');
  if (!trigger) return;

  const selector = trigger.getAttribute('href');
  if (!selector || !selector.startsWith('#')) return;

  const target = document.querySelector(selector) || document.querySelector('.shopify-app-block');
  if (!target) return;

  event.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

/* The quantity stepper sits on the same row as Add to bag. Dawn renders it as a
   separate buy-box block, several levels of markup away from the button, so
   there is no CSS pairing available — it has to be moved into
   .product-form__buttons, where a two-column grid puts the two side by side and
   leaves the dynamic checkout button spanning underneath.

   Moving rather than re-rendering it keeps Dawn's <quantity-input> behaviour and
   its volume-pricing rules intact. Dawn replaces the section's markup on every
   variant change, which puts a fresh stepper back in its original position, so
   this re-runs on mutation rather than once on load. */
(() => {
  const place = () => {
    const buttons = document.querySelector('product-info .product-form__buttons');
    const quantity = document.querySelector('product-info .product-form__quantity');
    if (!buttons || !quantity || quantity.parentElement === buttons) return;
    buttons.prepend(quantity);
  };

  const start = () => {
    const host = document.querySelector('product-info');
    if (!host) return;
    place();
    /* The guard above makes this idempotent, so the observer's own mutation
       can't loop back on itself. */
    new MutationObserver(place).observe(host, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* An emptied quantity field reverts to the product's minimum on the way out.
   Dawn validates the stepper buttons but leaves a cleared field empty, which
   then posts no quantity at all.

   Corrected on focusout rather than on input, so a value can be deleted and
   retyped without the field fighting back mid-edit. The change event is
   re-dispatched because Dawn listens for it to update volume pricing; it
   cannot recurse, since the corrected value passes the check on the way back
   in. */
document.addEventListener('focusout', (event) => {
  const input = event.target.closest?.('product-info .quantity__input');
  if (!input) return;

  const min = Number(input.getAttribute('min')) || 1;
  const value = input.value.trim();
  const parsed = Number(value);
  if (value !== '' && Number.isFinite(parsed) && parsed >= min) return;

  input.value = String(min);
  input.dispatchEvent(new Event('change', { bubbles: true }));
});
